import { PageConfig } from "@front-apps/core/models/pageConfig"
import { CwComponentConfig } from "@front-apps/core/models/pageConfig"
import React, { useState } from "react"
import {
    TextField,
    InputAdornment,
    Box,
    Select,
    MenuItem,
    SelectChangeEvent,
} from "@mui/material"
import { styled } from "@mui/system"
import { useTranslation } from "react-i18next"
import { Color } from "@front-apps/core/models/colors"
import {
    convertHeight,
    convertShoeSize,
    convertWeight,
} from "../utils/conversions" // Import the function
const StyledTextField = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        width: "100%",
        borderRadius: "4px",
        backgroundColor: "transparent",
        "& fieldset": {
            borderColor: Color.checkInPurple,
        },
        "&:hover fieldset": {
            borderColor: Color.checkInPurple,
        },
        "&.Mui-focused fieldset": {
            borderColor: Color.checkInPurple,
        },
    },
})

const CwMultiUnitInput = (
    props: PageConfig<CwComponentConfig.MultiUnitInput>
) => {
    const { config } = props
    const { className, onChange } = config
    const [value, setValue] = useState<any>("")
    const unit = props.config?.unit || "weight"
    const getUnitLabel = (unit: "weight" | "height" | "shoeSize") => {
        switch (unit) {
            case "weight":
                return "Kg"
            case "height":
                return "Cm"
            case "shoeSize":
                return "EU"
            default:
                return ""
        }
    }

    const [unitLabel, setUnitLabel] = useState(getUnitLabel(unit))

    // Add this function to get available units
    const getUnitOptions = (unit: "weight" | "height" | "shoeSize") => {
        switch (unit) {
            case "weight":
                return ["Kg", "Stone", "Pound"]
            case "height":
                return ["Cm", "Inch"]
            case "shoeSize":
                return ["EU", "UK", "US-K", "US-M", "US-W", "MP"]
            default:
                return []
        }
    }
    const { t } = useTranslation()

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value)

        const inputValue = event.target.value

        if (inputValue === "") {
            onChange &&
                onChange({
                    value: "",
                    unit: unitLabel,
                })
            return
        }

        switch (unit) {
            case "weight":
                const weightInKg = convertWeight(inputValue, unitLabel, "Kg")

                onChange &&
                    onChange({
                        value: Math.round(Number(weightInKg)),
                        unit: "Kg",
                    })
                break

            case "height":
                const heightInCm = convertHeight(inputValue, unitLabel, "Cm")

                onChange &&
                    onChange({
                        value: Math.round(Number(heightInCm)),
                        unit: "Cm",
                    })
                break

            case "shoeSize":
                const shoeSizeInEU = convertShoeSize(
                    unitLabel,
                    "EU",
                    inputValue
                )

                onChange &&
                    onChange({
                        value: shoeSizeInEU,
                        unit: "EU",
                    })
                break

            default:
                setValue(inputValue)
                onChange &&
                    onChange({
                        value: inputValue,
                        unit: unitLabel,
                    })
                break
        }
    }

    const handleUnitChange = (event: SelectChangeEvent<string>) => {
        if (getUnitOptions(unit).includes(event.target.value)) {
            setUnitLabel(event.target.value)
        }

        switch (unit) {
            case "weight":
                setValue(convertWeight(value, unitLabel, event.target.value))
                const weightInKg = convertWeight(value, unitLabel, "Kg")
                onChange &&
                    onChange({
                        value: weightInKg,
                        unit: "Kg",
                    })
                break
            case "height":
                setValue(convertHeight(value, unitLabel, event.target.value))
                const heightInCm = convertHeight(value, unitLabel, "Cm")
                onChange &&
                    onChange({
                        value: heightInCm,
                        unit: "Cm",
                    })
                break
            case "shoeSize":
                setValue(convertShoeSize(unitLabel, event.target.value, value))
                const shoeSizeInEU = convertShoeSize(unitLabel, "EU", value)
                onChange &&
                    onChange({
                        value: shoeSizeInEU,
                        unit: "EU",
                    })
                break
        }
    }

    return (
        <StyledTextField
            fullWidth
            value={value}
            onChange={handleChange}
            label={t(config.unit)}
            className={className}
            required={config.required}
            error={config.error}
            helperText={config.helperText}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <Select
                            value={unitLabel}
                            variant="standard"
                            onChange={handleUnitChange}
                            sx={{
                                "&:before": {
                                    borderBottom: "none",
                                },
                                "&:after": {
                                    borderBottom: "none",
                                },
                                "& .MuiSelect-select": {
                                    paddingRight: "24px",
                                },
                            }}
                        >
                            {getUnitOptions(unit).map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </InputAdornment>
                ),
            }}
        />
    )
}

export default CwMultiUnitInput
